import { Fragment } from 'react'
import useEmpresa from '@/contexts/useEmpresa'
import FaturaItem from './FaturaItem'
import FaturaValorResumo from './FaturaValorResumo'
import {
    formaPagamentoPadrao,
    formatDetalheCategoria,
    formatDetalheValor,
    getDescontoAntecipadoAplicavel,
    getJurosAplicavel,
    getMultaAplicavel,
    getTaxasMetodoPagamento,
    getValorComDescontoAntecipado,
    groupDetalhesAtendimento,
    isDetalheNegativo,
} from '@/services/fatura'
import type { DetalheFatura, Fatura } from '@/types/fatura'
import { formatDate, formatMoney } from '@/utils'
import './FaturaItem.scss'

interface FaturasAbertasCardProps {
    faturas: Fatura[]
}

function toDate(value?: string) {
    if (!value) return null
    const parsed = new Date(value.replace(/-/g, '/'))
    return Number.isNaN(parsed.getTime()) ? null : parsed
}

function formatInlineDate(value?: string) {
    const parsed = toDate(value)
    if (!parsed) return ''
    return parsed.toLocaleDateString('pt-BR')
}

function getDueLabel(fatura: Fatura) {
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const due = toDate(fatura.DataVencimento)
    if (!due) return ''

    const dueDate = new Date(due)
    dueDate.setHours(0, 0, 0, 0)

    const diffDays = Math.round((dueDate.getTime() - today.getTime()) / 86400000)

    if (diffDays < 0) return `Vencida há ${Math.abs(diffDays)} dia${Math.abs(diffDays) === 1 ? '' : 's'}`
    if (diffDays === 0) return 'Vence hoje'
    return `Vence em ${diffDays} dia${diffDays === 1 ? '' : 's'}`
}

function FaturaDescontoRow({ desconto }: { desconto: number }) {
    return (
        <tr className="fatura-aberta-row-desconto">
            <td className="fatura-col-id" />
            <td className="fatura-aberta-desconto-label">Desconto</td>
            <td className="text-right fatura-aberta-desconto-valor">
                - {formatMoney(desconto)}
            </td>
        </tr>
    )
}

function FaturaAcrescimoRow({ label, valor }: { label: string; valor: number }) {
    return (
        <tr className="fatura-aberta-row-acrescimo">
            <td className="fatura-col-id" />
            <td className="fatura-aberta-acrescimo-label">{label}</td>
            <td className="text-right fatura-aberta-acrescimo-valor">
                + {formatMoney(valor)}
            </td>
        </tr>
    )
}

function AtendimentoHeaderRow({
    codigo,
    vencimento,
    dueLabel,
    dueClass,
    categoria,
    titulo,
    valor,
    hasDetails,
}: {
    codigo?: number | null
    vencimento: string
    dueLabel: string
    dueClass: string
    categoria: string
    titulo: string
    valor: string
    hasDetails: boolean
}) {
    const overdue = dueClass.includes('is-overdue')

    return (
        <tr className={`fatura-aberta-row-header ${dueClass}${hasDetails ? ' has-details' : ''}`}>
                            <td className="fatura-col-id">
                {codigo ? <span className="fatura-aberta-id">#{codigo}</span> : null}
                <div className="fatura-aberta-venc">{formatDate(vencimento)}</div>
                {dueLabel ? (
                    <small className={`fatura-aberta-venc-status${overdue ? ' is-overdue' : ''}`}>
                        {dueLabel}
                    </small>
                ) : null}
            </td>
            <td>
                {categoria ? <div className="fatura-aberta-category">{categoria}</div> : null}
                <div className="fatura-aberta-title">{titulo}</div>
            </td>
            <td className="text-right">
                {valor ? <strong className="fatura-aberta-valor">{valor}</strong> : null}
            </td>
        </tr>
    )
}

function DetalheServicoRow({
    item,
    isFirst,
    isLast,
}: {
    item: DetalheFatura
    isFirst?: boolean
    isLast?: boolean
}) {
    const dateText = formatInlineDate(item.Data)
    const valorExibido = formatDetalheValor(item, formatMoney)
    const negativo = isDetalheNegativo(item)
    const isPlano = Boolean(item.Plano)

    return (
        <tr
            className={[
                'fatura-detalhe-row',
                isFirst ? 'is-first' : '',
                isLast ? 'is-last' : '',
                negativo ? 'is-negative' : '',
            ].filter(Boolean).join(' ')}
        >
            <td className="fatura-col-id" aria-hidden="true" />
            <td>
                <div className="fatura-detalhe-line">
                    {dateText ? <span className="fatura-detalhe-date">{dateText}</span> : null}
                    <span className="fatura-detalhe-desc">{item.Descricao}</span>
                </div>
            </td>
            <td
                className={[
                    'text-right',
                    'fatura-detalhe-valor',
                    negativo ? 'is-negative' : '',
                    isPlano ? 'is-plano' : '',
                ].filter(Boolean).join(' ')}
            >
                {valorExibido}
            </td>
        </tr>
    )
}

function OutroDetalheRow({ item, data }: { item: DetalheFatura; data?: string }) {
    const dateText = formatInlineDate(item.Data ?? data)
    const itemCode = item.Index ?? item.CdClientePlano
    const valorExibido = formatDetalheValor(item, formatMoney)
    const negativo = isDetalheNegativo(item)
    const isPlano = Boolean(item.Plano)

    return (
        <tr className={`fatura-detalhe-row${negativo ? ' is-negative' : ''}`}>
            <td className="fatura-col-id">
                {itemCode ? <span className="fatura-aberta-id">#{itemCode}</span> : null}
            </td>
            <td>
                {dateText ? <div className="fatura-detalhe-date">{dateText}</div> : null}
                <div className="fatura-detalhe-desc">{item.Descricao}</div>
                {item.Animais ? <div className="fatura-detalhe-animais">{item.Animais}</div> : null}
            </td>
            <td
                className={[
                    'text-right',
                    'fatura-detalhe-valor',
                    negativo ? 'is-negative' : '',
                    isPlano ? 'is-plano' : '',
                ].filter(Boolean).join(' ')}
            >
                {valorExibido}
            </td>
        </tr>
    )
}

export default function FaturasAbertasCard({ faturas }: FaturasAbertasCardProps) {
    const { empresa } = useEmpresa()
    const taxas = getTaxasMetodoPagamento(empresa, formaPagamentoPadrao(empresa))

    if (faturas.length === 0) return null

    const primeiraFatura = faturas[0]

    return (
        <div className="faturas-aberta-card">
            <table className="fatura-aberta-table faturas-desktop-only">
                <thead>
                    <tr>
                        <th style={{ width: 128 }}>Código</th>
                        <th>Descrição</th>
                        <th className="text-right" style={{ width: 180 }}>Valor</th>
                    </tr>
                </thead>
                <tbody>
                    {faturas.map((fatura, index) => {
                        const detalhes = fatura.Detalhes ?? []
                        const { grupos, outros } = groupDetalhesAtendimento(detalhes)
                        const dueLabel = getDueLabel(fatura)
                        const descontoAntecipado = getDescontoAntecipadoAplicavel(fatura)
                        const multa = getMultaAplicavel(fatura, taxas)
                        const juros = getJurosAplicavel(fatura, taxas)
                        const valorComDesconto = getValorComDescontoAntecipado(fatura) + multa + juros
                        const dueClass =
                            dueLabel.startsWith('Vencida')
                                ? 'is-overdue'
                                : index === 0
                                    ? 'is-earliest'
                                    : ''

                        return (
                            <Fragment key={fatura.CdFatura}>
                                {index > 0 && (
                                    <tr className="fatura-group-spacer" aria-hidden="true">
                                        <td colSpan={3} />
                                    </tr>
                                )}

                                {grupos.map((grupo, grupoIndex) => {
                                    const usoDate = formatInlineDate(grupo.principal.Data)
                                    const categoria = formatDetalheCategoria(grupo.principal, usoDate)
                                    const titulo =
                                        grupo.principal.Tipo === 2
                                            ? (grupo.principal.Frequencia || grupo.principal.Descricao || fatura.Titulo)
                                            : (grupo.principal.Descricao || fatura.Titulo)
                                    const mostrarValorFatura = grupoIndex === 0
                                    const itensDetalhe =
                                        grupo.principal.Tipo === 2
                                            ? grupo.itens.filter(item => !item.Principal)
                                            : grupo.itens

                                    return (
                                        <Fragment key={`${fatura.CdFatura}-${grupo.index}`}>
                                            <AtendimentoHeaderRow
                                                codigo={grupo.index}
                                                vencimento={fatura.DataVencimento}
                                                dueLabel={grupoIndex === 0 ? dueLabel : ''}
                                                dueClass={dueClass}
                                                categoria={categoria}
                                                titulo={titulo || ''}
                                                valor={mostrarValorFatura ? formatMoney(valorComDesconto) : ''}
                                                hasDetails={itensDetalhe.length > 0}
                                            />
                                            {itensDetalhe.map((item, detalheIndex) => (
                                                <DetalheServicoRow
                                                    key={`${fatura.CdFatura}-${grupo.index}-${detalheIndex}`}
                                                    item={item}
                                                    isFirst={detalheIndex === 0}
                                                    isLast={detalheIndex === itensDetalhe.length - 1}
                                                />
                                            ))}
                                        </Fragment>
                                    )
                                })}

                                {grupos.length === 0 && (
                                    <AtendimentoHeaderRow
                                        codigo={outros[0]?.CdClientePlano ?? outros[0]?.Index}
                                        vencimento={fatura.DataVencimento}
                                        dueLabel={dueLabel}
                                        dueClass={dueClass}
                                        categoria=""
                                        titulo={fatura.Titulo}
                                        valor={formatMoney(valorComDesconto)}
                                        hasDetails={outros.length > 0}
                                    />
                                )}

                                {outros.map((item, detalheIndex) => (
                                    <OutroDetalheRow
                                        key={`${fatura.CdFatura}-outro-${detalheIndex}`}
                                        item={item}
                                        data={fatura.DataVencimento}
                                    />
                                ))}

                                {descontoAntecipado > 0 && (
                                    <FaturaDescontoRow desconto={descontoAntecipado} />
                                )}
                                {multa > 0 && (
                                    <FaturaAcrescimoRow label="Multa" valor={multa} />
                                )}
                                {juros > 0 && (
                                    <FaturaAcrescimoRow label="Juros" valor={juros} />
                                )}
                            </Fragment>
                        )
                    })}
                </tbody>
            </table>

            <div className="faturas-mobile-only fatura-item-list">
                {faturas.map(fatura => (
                    <FaturaItem key={fatura.CdFatura} fatura={fatura} />
                ))}
            </div>

            <table className="faturas-aberta-meta-table faturas-aberta-meta-table-bottom">
                <tbody>
                    <tr>
                        <td colSpan={2}>
                            <FaturaValorResumo faturas={faturas} variant="panel" />
                        </td>
                    </tr>
                    <tr>
                        <td>Vencimento</td>
                        <td className="text-right">
                            <strong>{formatDate(primeiraFatura.DataVencimento)}</strong>
                            <small>{getDueLabel(primeiraFatura)}</small>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    )
}
